/**
 * BingoHub — the Bingo landing at /bingo.
 *
 * The resident picks how to play, then drops straight into the game (no
 * intermediate screen — playbook rule #5). Two ways in:
 *   - TODAY'S GAME — the daily-seeded game, "the same one everyone's playing"
 *     in the community today. This is the social glue.
 *   - NEW GAME — a fresh reseed for practice, any time.
 * Plus two quiet choices that carry into the game: the winning PATTERN and how
 * many CARDS to play (1 is the kind default).
 *
 * Composition: a community hall — felt header rail, the caller's medallion
 * teasing "B 7", warm cards on the table.
 */
import { motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { usePreferences } from '@/preferences';

import { MAX_CARDS, PATTERNS, type Pattern } from '@rendever/puzzle-core/bingo';
import { cardSpring, HUB_STAGGER_S, hoverSpring, pageSpring } from './motion';
import { sound } from './sound';

import './BingoHub.css';

const CARD_COUNTS = [1, 2, 3, 4] as const;

export function BingoHub(): React.JSX.Element {
  const navigate = useNavigate();
  const { theme } = usePreferences();

  const [pattern, setPattern] = useState<Pattern>('line');
  const [cards, setCards] = useState<number>(1);
  const [autoDaub, setAutoDaub] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => {
      sound.unlock();
      sound.play('whoosh');
    }, 80);
    return () => window.clearTimeout(t);
  }, []);

  const start = (mode: 'daily' | 'practice'): void => {
    sound.unlock();
    sound.play('whoosh');
    const q = new URLSearchParams({
      mode,
      pattern,
      cards: String(cards),
      ...(autoDaub ? { autodaub: '1' } : {}),
    });
    navigate(`/play/bingo?${q.toString()}`);
  };

  return (
    <div className="bingo-hub" data-theme={theme}>
      <div className="bingo-hub__hall" aria-hidden="true" />

      <div className="bingo-hub__inner">
        <motion.header
          className="bingo-hub__head"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...pageSpring, delay: 0.04 }}
        >
          <div className="bingo-hub__head-text">
            <p className="bingo-hub__eyebrow">Rendever Bingo · The hall game</p>
            <h1 className="bingo-hub__title">Bingo.</h1>
            <p className="bingo-hub__lede">
              The caller reads the numbers; you find them on your card and mark
              them off. Gentle pace, no rush — and you can pause whenever you
              like. Five in a row and you call it: <strong>Bingo!</strong>
            </p>
          </div>

          <motion.div
            className="bingo-hub__medallion"
            initial={{ opacity: 0, scale: 0.8, rotate: -6 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ ...cardSpring, delay: 0.12 }}
            aria-hidden="true"
          >
            <span className="bingo-hub__medallion-letter">B</span>
            <span className="bingo-hub__medallion-number">7</span>
          </motion.div>
        </motion.header>

        <motion.section
          className="bingo-hub__choices"
          aria-label="Set up your game"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...pageSpring, delay: 0.14 }}
        >
          {/* Pattern */}
          <fieldset className="bingo-hub__field">
            <legend className="bingo-hub__field-title">Winning pattern</legend>
            <div className="bingo-hub__pattern-row" role="radiogroup" aria-label="Winning pattern">
              {PATTERNS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={pattern === p.id}
                  className="bingo-hub__pattern"
                  data-active={pattern === p.id ? 'true' : 'false'}
                  onClick={() => {
                    setPattern(p.id);
                    sound.play('tap');
                  }}
                >
                  <PatternGlyph id={p.id} />
                  <span className="bingo-hub__pattern-name">{p.title}</span>
                  <span className="bingo-hub__pattern-blurb">{p.blurb}</span>
                </button>
              ))}
            </div>
          </fieldset>

          {/* Cards + auto-daub */}
          <div className="bingo-hub__field-grid">
            <fieldset className="bingo-hub__field">
              <legend className="bingo-hub__field-title">How many cards</legend>
              <div className="bingo-hub__cards-row" role="radiogroup" aria-label="Number of cards">
                {CARD_COUNTS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={cards === n}
                    className="bingo-hub__count"
                    data-active={cards === n ? 'true' : 'false'}
                    onClick={() => {
                      setCards(n);
                      sound.play('tap');
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <p className="bingo-hub__field-hint">
                {cards === 1 ? 'One card is plenty — easy to follow.' : `${cards} cards, ${MAX_CARDS === cards ? 'a real hall hand' : 'a bigger game'}.`}
              </p>
            </fieldset>

            <fieldset className="bingo-hub__field">
              <legend className="bingo-hub__field-title">Marking</legend>
              <label className="bingo-hub__toggle">
                <input
                  type="checkbox"
                  checked={autoDaub}
                  onChange={(e) => {
                    setAutoDaub(e.target.checked);
                    sound.play('tap');
                  }}
                />
                <span className="bingo-hub__toggle-track" aria-hidden="true">
                  <span className="bingo-hub__toggle-knob" />
                </span>
                <span className="bingo-hub__toggle-text">
                  <span className="bingo-hub__toggle-name">Mark for me</span>
                  <span className="bingo-hub__toggle-hint">
                    Numbers mark themselves — sit back and watch the card fill.
                  </span>
                </span>
              </label>
            </fieldset>
          </div>
        </motion.section>

        {/* Start cards */}
        <section className="bingo-hub__starts" aria-label="Start a game">
          <ul className="bingo-hub__start-list" role="list">
            <motion.li
              className="bingo-hub__start-item"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...cardSpring, delay: 0.2 }}
              whileHover={{ y: -4, transition: hoverSpring }}
            >
              <button
                type="button"
                className="bingo-hub__start bingo-hub__start--daily"
                onClick={() => start('daily')}
                onPointerEnter={() => sound.play('tap')}
              >
                <span className="bingo-hub__start-eyebrow">Today, together</span>
                <span className="bingo-hub__start-title">Today&rsquo;s game</span>
                <span className="bingo-hub__start-blurb">
                  The same game everyone in the community is playing today. Chat
                  about it over lunch.
                </span>
                <span className="bingo-hub__start-cta" aria-hidden="true">
                  Play today&rsquo;s game →
                </span>
              </button>
            </motion.li>

            <motion.li
              className="bingo-hub__start-item"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...cardSpring, delay: 0.2 + HUB_STAGGER_S }}
              whileHover={{ y: -4, transition: hoverSpring }}
            >
              <button
                type="button"
                className="bingo-hub__start bingo-hub__start--practice"
                onClick={() => start('practice')}
                onPointerEnter={() => sound.play('tap')}
              >
                <span className="bingo-hub__start-eyebrow">Any time</span>
                <span className="bingo-hub__start-title">New game</span>
                <span className="bingo-hub__start-blurb">
                  A fresh game just for you, as many as you like. No streak, no
                  clock.
                </span>
                <span className="bingo-hub__start-cta" aria-hidden="true">
                  Start a new game →
                </span>
              </button>
            </motion.li>
          </ul>
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pattern glyphs — 5×5 mini-grids showing the shape. Structural geometry.
// ---------------------------------------------------------------------------

const GLYPH_CELLS: Record<Pattern, ReadonlyArray<number>> = {
  line: [0, 1, 2, 3, 4],
  'four-corners': [0, 4, 20, 24],
  blackout: Array.from({ length: 25 }, (_, i) => i),
};

function PatternGlyph({ id }: { id: Pattern }): React.JSX.Element {
  const lit = new Set(GLYPH_CELLS[id]);
  return (
    <span className="bingo-hub__glyph" aria-hidden="true">
      {Array.from({ length: 25 }, (_, i) => (
        <span
          key={i}
          className="bingo-hub__glyph-cell"
          data-lit={lit.has(i) ? 'true' : 'false'}
          data-free={i === 12 ? 'true' : 'false'}
        />
      ))}
    </span>
  );
}
